import { useEffect, useState, useMemo, useRef, useCallback } from "react";

/* ------------------------------------------------------------------ */
/*  Scroll-reveal hook                                                 */
/* ------------------------------------------------------------------ */

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return { ref, visible };
}

/* ------------------------------------------------------------------ */
/*  Animated counter                                                   */
/* ------------------------------------------------------------------ */

function useAnimatedNumber(target: number, duration = 1800) {
  const [value, setValue] = useState(0);
  const triggered = useRef(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered.current) {
          triggered.current = true;
          const start = performance.now();
          const tick = (now: number) => {
            const t = Math.min((now - start) / duration, 1);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(Math.round(eased * target));
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.5 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return { ref, value };
}

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const benefits = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    label: "Lifetime Platform Access",
    desc: "Every feature we ship, for as long as Genie runs. No monthly fees, no price hikes.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
    label: "$1,000 in Platform Credits",
    desc: "Loaded to your wallet the moment you join. Use on any model. They never expire.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    label: "15% Referral Program",
    desc: "When people you invite spend credits, you earn 15%. Withdrawable as cash or credits.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
    label: "Priority Support",
    desc: "Skip the queue. Dedicated channel to the people building the platform.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
    label: "Early Feature Access",
    desc: "New models, new nodes, new tools — founding members always get them first.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2z" />
      </svg>
    ),
    label: "Founding Member Badge",
    desc: "Permanent profile badge. Access to the exclusive founding community.",
  },
];

const timeline = [
  {
    phase: "01",
    title: "Join",
    desc: "One payment. Instant access, $1,000 in credits, and your unique referral link.",
  },
  {
    phase: "02",
    title: "Create",
    desc: "Build workflows with 1,000+ AI models. You see the price before you run — in dollars, not tokens.",
  },
  {
    phase: "03",
    title: "Share",
    desc: "Send your link. When your referrals create, you earn 15% of what they spend.",
  },
  {
    phase: "04",
    title: "Grow",
    desc: "As the community grows, founding members are first in line for every new opportunity.",
  },
];

const faqs: { q: string; a: string }[] = [
  {
    q: "What does \"lifetime\" actually mean?",
    a: "Access to all current and future Genie features for as long as the platform is operational. This is a product purchase, not a subscription. Your access cannot be downgraded or repriced.",
  },
  {
    q: "Do credits expire?",
    a: "No. Credits stay in your wallet as long as your account is active. No monthly minimums, no use-it-or-lose-it tricks.",
  },
  {
    q: "How do referral earnings work?",
    a: "You get a unique link. When someone signs up through it and spends credits on AI jobs, you earn 15% of that spend. Withdraw as cash (min $50) or convert to credits.",
  },
  {
    q: "Is this an investment?",
    a: "No. The $1,000 is a product purchase — platform access plus credits. Any future equity opportunity will be a separate, optional, regulated transaction.",
  },
  {
    q: "What if I change my mind?",
    a: "Full refund within 30 days. No questions, no forms, no hoops. After 30 days, your credits remain but the purchase is non-refundable.",
  },
  {
    q: "Is there a cap on referral earnings?",
    a: "No cap on total earnings. There is a per-referral lifetime cap of $2,000. Actual earnings depend on how actively your referrals use the platform. Results vary.",
  },
  {
    q: "What about the future equity opportunity?",
    a: "We plan to explore a community equity round through a regulated platform like Republic or Wefunder. Founding members would get priority notification. It is entirely separate from this membership — different transaction, different terms, subject to securities regulations.",
  },
  {
    q: "Is this available outside the US?",
    a: "The membership and referral program are available globally. Any future equity opportunity will initially be US-only due to securities regulations.",
  },
];

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function FAQ({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  const r = useReveal(0.1);

  return (
    <div
      ref={r.ref}
      className="border-b border-black/[0.06] transition-all duration-700"
      style={{
        opacity: r.visible ? 1 : 0,
        transform: r.visible ? "translateY(0)" : "translateY(12px)",
        transitionDelay: `${index * 60}ms`,
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="group w-full flex items-center justify-between py-6 text-left cursor-pointer"
      >
        <span className="text-[15px] font-medium text-[#111] pr-8 group-hover:text-[#555] transition-colors">
          {q}
        </span>
        <span
          className="w-7 h-7 rounded-full border border-black/[0.08] flex items-center justify-center shrink-0 transition-all duration-300 group-hover:border-black/20"
          style={{ transform: open ? "rotate(45deg)" : "rotate(0deg)" }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round">
            <line x1="6" y1="2" x2="6" y2="10" />
            <line x1="2" y1="6" x2="10" y2="6" />
          </svg>
        </span>
      </button>
      <div
        className="grid transition-all duration-300 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <p className="text-[14px] text-[#888] leading-[1.8] pb-6">{a}</p>
        </div>
      </div>
    </div>
  );
}

function BenefitCard({
  icon,
  label,
  desc,
  index,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
  index: number;
}) {
  const r = useReveal(0.1);

  return (
    <div
      ref={r.ref}
      className="group relative p-6 rounded-2xl transition-all duration-500 hover:bg-white/60"
      style={{
        opacity: r.visible ? 1 : 0,
        transform: r.visible ? "translateY(0)" : "translateY(20px)",
        transitionDelay: `${index * 80}ms`,
      }}
    >
      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-[#999] mb-4 transition-colors duration-300 group-hover:text-[#111] shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        {icon}
      </div>
      <h3 className="text-[16px] font-medium text-[#111] mb-2">{label}</h3>
      <p className="text-[13px] text-[#888] leading-[1.7]">{desc}</p>
    </div>
  );
}

function TimelineStep({
  step,
  index,
  isLast,
}: {
  step: { phase: string; title: string; desc: string };
  index: number;
  isLast: boolean;
}) {
  const r = useReveal(0.1);

  return (
    <div
      ref={r.ref}
      className={`group flex gap-6 md:gap-10 py-8 transition-all duration-600 ${
        !isLast ? "border-b border-black/[0.06]" : ""
      }`}
      style={{
        opacity: r.visible ? 1 : 0,
        transform: r.visible ? "translateX(0)" : "translateX(-20px)",
        transitionDelay: `${index * 100}ms`,
      }}
    >
      <div className="text-[36px] font-light text-[#ddd] tracking-[-0.02em] shrink-0 w-[50px] transition-colors duration-300 group-hover:text-[#bbb]">
        {step.phase}
      </div>
      <div>
        <h3 className="text-[18px] font-medium text-[#111] mb-1.5">
          {step.title}
        </h3>
        <p className="text-[14px] text-[#888] leading-[1.7]">
          {step.desc}
        </p>
      </div>
    </div>
  );
}

function MathCard({
  value,
  label,
  desc,
  index,
}: {
  value: string;
  label: string;
  desc: string;
  index: number;
}) {
  const r = useReveal(0.1);

  return (
    <div
      ref={r.ref}
      className="group bg-[#f0efeb] rounded-2xl p-8 transition-all duration-500 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:-translate-y-1"
      style={{
        opacity: r.visible ? 1 : 0,
        transform: r.visible ? "translateY(0)" : "translateY(24px)",
        transitionDelay: `${index * 120}ms`,
      }}
    >
      <div className="text-[44px] font-light tracking-[-0.03em] text-[#111] mb-2 transition-transform duration-300 group-hover:scale-[1.02] origin-left">
        {value}
      </div>
      <div className="text-[12px] font-medium text-[#111] uppercase tracking-[0.08em] mb-3">
        {label}
      </div>
      <p className="text-[13px] text-[#999] leading-[1.65]">{desc}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Referral calculator                                                */
/* ------------------------------------------------------------------ */

function ReferralCalculator() {
  const [creators, setCreators] = useState(10);
  const [spend, setSpend] = useState(100);
  const monthly = creators * spend * 0.15;
  const yearly = monthly * 12;
  const r = useReveal(0.1);

  const handleSlider = useCallback((setter: (v: number) => void) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => setter(Number(e.target.value));
  }, []);

  return (
    <div
      ref={r.ref}
      className="transition-all duration-700"
      style={{
        opacity: r.visible ? 1 : 0,
        transform: r.visible ? "translateY(0)" : "translateY(24px)",
      }}
    >
      <div className="bg-[#f0efeb] rounded-2xl p-8 md:p-12">
        {/* Sliders */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div>
            <div className="flex items-baseline justify-between mb-3">
              <label className="text-[12px] text-[#999] uppercase tracking-[0.08em]">
                Creators you refer
              </label>
              <span className="text-[28px] font-light tracking-[-0.03em] text-[#111] tabular-nums">
                {creators}
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={100}
              value={creators}
              onChange={handleSlider(setCreators)}
              className="w-full accent-[#111] h-[2px] appearance-none bg-black/10 rounded-full cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#111] [&::-webkit-slider-thumb]:shadow-[0_1px_4px_rgba(0,0,0,0.2)] [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-150 [&::-webkit-slider-thumb]:hover:scale-125"
            />
            <div className="flex justify-between mt-1.5">
              <span className="text-[10px] text-[#ccc]">1</span>
              <span className="text-[10px] text-[#ccc]">100</span>
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-3">
              <label className="text-[12px] text-[#999] uppercase tracking-[0.08em]">
                Their avg. monthly spend
              </label>
              <span className="text-[28px] font-light tracking-[-0.03em] text-[#111] tabular-nums">
                ${spend}
              </span>
            </div>
            <input
              type="range"
              min={10}
              max={500}
              step={10}
              value={spend}
              onChange={handleSlider(setSpend)}
              className="w-full accent-[#111] h-[2px] appearance-none bg-black/10 rounded-full cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#111] [&::-webkit-slider-thumb]:shadow-[0_1px_4px_rgba(0,0,0,0.2)] [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-150 [&::-webkit-slider-thumb]:hover:scale-125"
            />
            <div className="flex justify-between mt-1.5">
              <span className="text-[10px] text-[#ccc]">$10</span>
              <span className="text-[10px] text-[#ccc]">$500</span>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="border-t border-black/[0.06] pt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] text-[#bbb] uppercase tracking-[0.1em] mb-1">
                Your monthly earnings
              </div>
              <div className="text-[40px] font-light tracking-[-0.03em] text-[#111] tabular-nums leading-none">
                ${monthly.toLocaleString()}
                <span className="text-[16px] text-[#ccc] font-normal">/mo</span>
              </div>
            </div>
            <div>
              <div className="text-[11px] text-[#bbb] uppercase tracking-[0.1em] mb-1">
                Projected yearly
              </div>
              <div className="text-[40px] font-light tracking-[-0.03em] text-[#111] tabular-nums leading-none">
                ${yearly.toLocaleString()}
                <span className="text-[16px] text-[#ccc] font-normal">/yr</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-black/[0.06] mt-8 pt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-[12px] text-[#999]">
            Withdraw as cash (min $50) or add to your credit balance.
          </p>
          <p className="text-[11px] text-[#ccc]">
            Illustrative only. Actual earnings depend on referral activity and are not guaranteed.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export function FoundingMember() {
  const [refCode, setRefCode] = useState("");
  const [navSolid, setNavSolid] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("ref");
    if (code) setRefCode(code);
  }, []);

  useEffect(() => {
    const onScroll = () => setNavSolid(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const waitlistHref = useMemo(
    () => (refCode ? `/waitlist?ref=${refCode}` : "/waitlist"),
    [refCode],
  );

  /* Animated counter */
  const counter = useAnimatedNumber(10000);

  /* Section reveals */
  const heroR = useReveal(0.05);
  const mathR = useReveal(0.1);
  const timelineR = useReveal(0.1);
  const refR = useReveal(0.1);
  const equityR = useReveal(0.1);
  const ctaR = useReveal(0.1);

  return (
    <div className="bg-white text-[#111] font-sans">
      {/* ── Navbar ── */}
      <nav
        className={`flex items-center justify-between h-[56px] px-8 sticky top-0 z-50 transition-all duration-300 ${
          navSolid
            ? "bg-white/90 backdrop-blur-xl shadow-[0_1px_8px_rgba(0,0,0,0.04)]"
            : "bg-transparent"
        }`}
      >
        <div className="flex items-center">
          <a href="#" className="flex items-center">
            <span className="font-semibold tracking-tight text-[17px] leading-none">Genie</span>
          </a>
          <div className="w-px h-[20px] bg-black/10 mx-4 hidden sm:block" />
          <span className="text-[11px] uppercase tracking-[0.12em] text-[#999] hidden sm:block">
            Founding Member
          </span>
        </div>
        <a
          href="#"
          className="text-[13px] text-[#999] hover:text-[#111] transition-colors"
        >
          Back to home
        </a>
      </nav>

      {/* ── Hero ── */}
      <section className="pt-20 pb-32 px-8 bg-[#f0efeb] overflow-hidden">
        <div
          ref={heroR.ref}
          className="w-full max-w-[900px] mx-auto text-center transition-all duration-1000"
          style={{
            opacity: heroR.visible ? 1 : 0,
            transform: heroR.visible ? "translateY(0)" : "translateY(30px)",
          }}
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-[#111] text-white text-[11px] uppercase tracking-[0.1em] mb-10">
            <div className="relative w-2 h-2">
              <div className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-40" />
              <div className="relative w-2 h-2 rounded-full bg-green-400" />
            </div>
            Limited to 10,000 creators
          </div>

          <h1
            className="font-light tracking-[-0.045em] text-[#111] mb-8"
            style={{ fontSize: "clamp(42px, 6.5vw, 96px)", lineHeight: "0.95" }}
          >
            The first 10,000
            <br />
            <span className="font-normal italic">get in forever.</span>
          </h1>

          <p className="text-[17px] leading-[1.75] text-[#888] max-w-[480px] mx-auto mb-12">
            One payment. Lifetime access to every feature we build.
            $1,000 in credits. A referral program that pays real money.
          </p>

          <a
            href={waitlistHref}
            className="group inline-flex items-center gap-3 bg-[#111] text-white rounded-full px-8 py-4 text-[15px] font-medium hover:bg-black transition-all duration-200 btn-invert"
          >
            Become a Founding Member
            <span className="text-white/30 font-light">$1,000</span>
          </a>

          <div className="mt-5 flex items-center justify-center gap-6 text-[12px] text-[#bbb]">
            <span>30-day full refund</span>
            <span className="w-1 h-1 rounded-full bg-[#ddd]" />
            <span>Cancel anytime</span>
            <span className="w-1 h-1 rounded-full bg-[#ddd]" />
            <span>No subscription</span>
          </div>
        </div>
      </section>

      {/* ── Counter ── */}
      <section className="py-24 px-8 bg-white">
        <div ref={counter.ref} className="text-center">
          <div
            className="font-light tracking-[-0.04em] text-[#111] tabular-nums"
            style={{ fontSize: "clamp(56px, 8vw, 120px)", lineHeight: "1" }}
          >
            {counter.value.toLocaleString()}
          </div>
          <p className="text-[14px] text-[#999] mt-3">spots remaining</p>
          {/* Progress bar */}
          <div className="max-w-[300px] mx-auto mt-6">
            <div className="h-[3px] bg-black/[0.06] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#111] rounded-full transition-all duration-[2s] ease-out"
                style={{ width: `${((10000 - counter.value) / 10000) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── What you get ── */}
      <section className="py-32 px-8 bg-[#f0efeb]">
        <div className="w-full max-w-[1060px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-[11px] uppercase tracking-[0.14em] text-[#bbb] mb-4">
              What&apos;s included
            </p>
            <h2
              className="font-light tracking-[-0.04em] text-[#111]"
              style={{ fontSize: "clamp(32px, 4vw, 56px)", lineHeight: "1.1" }}
            >
              Everything. No upsells.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {benefits.map((b, i) => (
              <BenefitCard key={b.label} {...b} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── The math ── */}
      <section className="py-32 px-8 bg-white">
        <div
          ref={mathR.ref}
          className="w-full max-w-[960px] mx-auto transition-all duration-700"
          style={{
            opacity: mathR.visible ? 1 : 0,
            transform: mathR.visible ? "translateY(0)" : "translateY(20px)",
          }}
        >
          <div className="text-center mb-16">
            <p className="text-[11px] uppercase tracking-[0.14em] text-[#bbb] mb-4">
              The breakdown
            </p>
            <h2
              className="font-light tracking-[-0.04em] text-[#111]"
              style={{ fontSize: "clamp(32px, 4vw, 56px)", lineHeight: "1.1" }}
            >
              Three reasons this works.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <MathCard
              value="$1,000"
              label="Credits"
              desc="Loaded instantly. At $0.03 per image, that's over 33,000 generations. Credits never expire."
              index={0}
            />
            <MathCard
              value="15%"
              label="Referral Rate"
              desc="When your referrals spend on AI jobs, you earn 15% of that spend. Withdraw as cash or credits."
              index={1}
            />
            <MathCard
              value="$0"
              label="Monthly Fee"
              desc="No subscription. Founding members get platform access included. Just pay for what you run."
              index={2}
            />
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-32 px-8 bg-[#f0efeb]">
        <div
          ref={timelineR.ref}
          className="w-full max-w-[800px] mx-auto transition-all duration-700"
          style={{
            opacity: timelineR.visible ? 1 : 0,
            transform: timelineR.visible ? "translateY(0)" : "translateY(20px)",
          }}
        >
          <div className="text-center mb-20">
            <p className="text-[11px] uppercase tracking-[0.14em] text-[#bbb] mb-4">
              Four steps
            </p>
            <h2
              className="font-light tracking-[-0.04em] text-[#111]"
              style={{ fontSize: "clamp(32px, 4vw, 56px)", lineHeight: "1.1" }}
            >
              How it works.
            </h2>
          </div>

          <div>
            {timeline.map((step, i) => (
              <TimelineStep key={step.phase} step={step} index={i} isLast={i === timeline.length - 1} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Referral calculator ── */}
      <section className="py-32 px-8 bg-white">
        <div className="w-full max-w-[900px] mx-auto">
          <div
            ref={refR.ref}
            className="text-center mb-16 transition-all duration-700"
            style={{
              opacity: refR.visible ? 1 : 0,
              transform: refR.visible ? "translateY(0)" : "translateY(20px)",
            }}
          >
            <p className="text-[11px] uppercase tracking-[0.14em] text-[#bbb] mb-4">
              Referral program
            </p>
            <h2
              className="font-light tracking-[-0.04em] text-[#111] mb-4"
              style={{ fontSize: "clamp(32px, 4vw, 56px)", lineHeight: "1.1" }}
            >
              Referrals that pay real money.
            </h2>
            <p className="text-[15px] text-[#888] max-w-[460px] mx-auto">
              Not points. Not badges. 15% of every dollar your referrals spend.
              Single-tier, no MLM — just honest commissions.
            </p>
          </div>

          <ReferralCalculator />
        </div>
      </section>

      {/* ── Equity section ── */}
      <section className="py-32 px-8 bg-[#111] text-white overflow-hidden selection-light">
        <div
          ref={equityR.ref}
          className="w-full max-w-[800px] mx-auto text-center transition-all duration-1000"
          style={{
            opacity: equityR.visible ? 1 : 0,
            transform: equityR.visible ? "translateY(0) scale(1)" : "translateY(20px) scale(0.98)",
          }}
        >
          <p className="text-[11px] uppercase tracking-[0.14em] text-white/25 mb-6">
            Separate Opportunity — Coming Later
          </p>
          <h2
            className="font-light tracking-[-0.04em] mb-6"
            style={{ fontSize: "clamp(32px, 4.5vw, 64px)", lineHeight: "1.1" }}
          >
            Community equity round.
          </h2>
          <p className="text-[15px] text-white/35 mb-10 max-w-[480px] mx-auto leading-[1.8]">
            We&apos;re exploring a future community equity round through a
            regulated platform. If and when it happens, founding members
            hear first. This is not part of the membership — it will be a
            separate, optional, regulated opportunity.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-white/20">
            <span>Not included in membership</span>
            <span className="w-1 h-1 rounded-full bg-white/10" />
            <span>Subject to regulations</span>
            <span className="w-1 h-1 rounded-full bg-white/10" />
            <span>Not guaranteed</span>
          </div>
          <p className="mt-10 text-[10px] text-white/10 max-w-[480px] mx-auto leading-[1.7]">
            No securities are being offered or sold on this page. Any future
            investment opportunity will be conducted through a registered
            intermediary in compliance with applicable securities laws.
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-32 px-8 bg-white">
        <div className="w-full max-w-[680px] mx-auto">
          <div className="text-center mb-16">
            <h2
              className="font-light tracking-[-0.04em] text-[#111]"
              style={{ fontSize: "clamp(32px, 4vw, 56px)", lineHeight: "1.1" }}
            >
              Questions.
            </h2>
          </div>

          <div>
            {faqs.map((f, i) => (
              <FAQ key={f.q} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-36 px-8 bg-[#f0efeb]">
        <div
          ref={ctaR.ref}
          className="w-full max-w-[800px] mx-auto text-center transition-all duration-1000"
          style={{
            opacity: ctaR.visible ? 1 : 0,
            transform: ctaR.visible ? "translateY(0)" : "translateY(30px)",
          }}
        >
          <h2
            className="font-light tracking-[-0.045em] text-[#111] mb-4"
            style={{ fontSize: "clamp(38px, 5.5vw, 80px)", lineHeight: "0.95" }}
          >
            10,000 spots.
          </h2>
          <h2
            className="font-light tracking-[-0.045em] text-black/15 mb-10"
            style={{ fontSize: "clamp(38px, 5.5vw, 80px)", lineHeight: "0.95" }}
          >
            Then it&apos;s closed.
          </h2>

          <p className="text-[16px] text-[#888] mb-12 max-w-[400px] mx-auto leading-[1.7]">
            Lifetime access. $1,000 in credits.
            15% referral earnings. One payment.
          </p>

          <a
            href={waitlistHref}
            className="group inline-flex items-center gap-3 bg-[#111] text-white rounded-full px-8 py-4 text-[15px] font-medium hover:bg-black transition-all duration-200 btn-invert"
          >
            Become a Founding Member
            <span className="text-white/30 font-light">$1,000</span>
          </a>

          <div className="mt-5 flex items-center justify-center gap-6 text-[12px] text-[#bbb]">
            <span>30-day full refund</span>
            <span className="w-1 h-1 rounded-full bg-[#ddd]" />
            <span>No questions asked</span>
          </div>
        </div>
      </section>

      {/* ── Legal disclaimer ── */}
      <section className="py-6 px-8 bg-[#fafafa] border-t border-black/[0.04]">
        <div className="w-full max-w-[700px] mx-auto">
          <p className="text-[10px] text-[#ccc] leading-[1.8]">
            The Founding Member program is a product purchase for platform access
            and credits. It is not an investment, security, or financial product.
            No securities are being offered or sold. Referral earnings depend on
            the activity of referred users and are not guaranteed. &quot;Lifetime
            access&quot; means access for as long as the Genie platform remains
            operational. Credits are non-transferable. Full terms available at
            purchase. 30-day refund policy applies to the initial purchase.
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-8 px-8 bg-white border-t border-black/[0.04]">
        <div className="w-full max-w-[1200px] mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="font-semibold tracking-tight text-[14px] leading-none">Genie</span>
            <span className="text-[12px] text-[#bbb]">
              Genie Art Inc. &copy; 2026
            </span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#" className="text-[12px] text-[#999] hover:text-[#111] transition-colors">
              Home
            </a>
            <a href="/waitlist" className="text-[12px] text-[#999] hover:text-[#111] transition-colors">
              Waitlist
            </a>
            <a href="#" className="text-[12px] text-[#999] hover:text-[#111] transition-colors">
              Privacy
            </a>
            <a href="#" className="text-[12px] text-[#999] hover:text-[#111] transition-colors">
              Terms
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
